import React from 'react';
import { Alert, Tabs } from 'antd';
import type { MonetizationFreshness } from '@/types';
import { AdsTab } from './AdsTab';
import { EconomyTab } from './EconomyTab';
import { ExperimentsTab } from './ExperimentsTab';
import { FunnelsTab } from './FunnelsTab';
import { SegmentsTab } from './SegmentsTab';
import { SkusTab } from './SkusTab';
import { SummaryTab } from './SummaryTab';
import { freshnessLine } from './monetizationMath';
import { PAGE_TABS, entryOf } from './monetizationTabState';
import type { MonetizationState, PageTab } from './monetizationTabState';

export const STALE_TITLE = 'The BigQuery data is behind';

const StaleBanner: React.FC<{ freshness: MonetizationFreshness }> = ({ freshness }) => (
  <div data-banner="stale">
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message={STALE_TITLE}
      description={`${freshnessLine(freshness)}. The latest days may be missing or incomplete.`}
    />
  </div>
);

export interface MonetizationBoardProps {
  state: MonetizationState;
  activeTab: PageTab;
  onTabChange(tab: PageTab): void;
  onRetry?: () => void;
  /** Experiment keys for the Experiments selector. */
  experimentKeys: string[];
  onExperimentChange(key: string | undefined): void;
}

/** The page below its controls: the stale banner and the tabs. Pure render of the page state. */
export const MonetizationBoard: React.FC<MonetizationBoardProps> = ({
  state,
  activeTab,
  onTabChange,
  onRetry,
  experimentKeys,
  onExperimentChange,
}) => {
  const content = (tab: PageTab): React.ReactNode => {
    switch (tab) {
      case 'summary':
        return <SummaryTab entry={entryOf(state, 'summary')} onRetry={onRetry} />;
      case 'skus':
        return <SkusTab entry={entryOf(state, 'skus')} onRetry={onRetry} />;
      case 'funnels':
        return (
          <FunnelsTab
            banners={entryOf(state, 'banners')}
            offers={entryOf(state, 'offers')}
            onRetry={onRetry}
          />
        );
      case 'ads':
        return (
          <AdsTab
            entry={entryOf(state, 'ads')}
            summary={entryOf(state, 'summary')}
            onRetry={onRetry}
          />
        );
      case 'economy':
        return <EconomyTab entry={entryOf(state, 'economy')} onRetry={onRetry} />;
      case 'segments':
        return <SegmentsTab entry={entryOf(state, 'segments')} onRetry={onRetry} />;
      case 'experiments':
        return (
          <ExperimentsTab
            entry={entryOf(state, 'experiments')}
            keys={experimentKeys}
            value={state.filters.experimentKey}
            onChange={onExperimentChange}
            onRetry={onRetry}
          />
        );
    }
  };
  return (
    <div>
      {state.freshness?.stale && <StaleBanner freshness={state.freshness} />}
      <Tabs
        activeKey={activeTab}
        onChange={(k) => onTabChange(k as PageTab)}
        items={PAGE_TABS.map((t) => ({
          key: t.key,
          label: t.label,
          children: t.key === activeTab ? <div data-tab={t.key}>{content(t.key)}</div> : null,
        }))}
      />
    </div>
  );
};
